/**
 * Arranges existing Jito field values into the Alfa9 Betting Records slots.
 * Does not change query builders, provider matching, or field sources.
 */

import {
  PAGE_SIZE,
  filterGameRows,
  filterSportsRows,
  gameFieldValues,
  gameHistoryQuery,
  providerChoices,
  sportsFieldValues,
  sportsHistoryQuery,
} from './jitoBettingFields.js';

export const DATE_PRESETS = Object.freeze(['today', 'yesterday', 'last7']);
export const DATE_PRESET_LABEL = Object.freeze({
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 days',
});

export const SETTLED_COLUMNS = Object.freeze(['Platform', 'Game Type', 'Turnover', 'Profit/Loss']);
export const UNSETTLED_COLUMNS = Object.freeze(['Platform', 'Game Type', 'Game', 'Bet']);

function isoDay(value) {
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function datePresetRange(preset, now = new Date()) {
  const key = DATE_PRESETS.includes(preset) ? preset : 'last7';
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  if (key === 'today') return { preset: key, from: isoDay(today), to: isoDay(today) };
  if (key === 'yesterday') {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    return { preset: key, from: isoDay(y), to: isoDay(y) };
  }
  const from = new Date(today);
  from.setDate(from.getDate() - 6);
  return { preset: 'last7', from: isoDay(from), to: isoDay(today) };
}

export function jitoAmount(value) {
  if (value == null || value === '' || value === '-') return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}`;
}

export function jitoText(value) {
  if (value == null) return '-';
  const text = String(value).trim();
  return text ? text : '-';
}

export function profitClass(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '';
  return n > 0 ? 'jbr-amt-pos' : 'jbr-amt-neg';
}

export function columnLabels(tab) {
  return tab === 'unsettled' ? UNSETTLED_COLUMNS : SETTLED_COLUMNS;
}

export function arrangeBettingRow(values, tab) {
  const source = values || {};
  const settled = tab !== 'unsettled';
  const cells = settled
    ? [
      jitoText(source.platform),
      jitoText(source.gameType),
      jitoAmount(source.turnover),
      jitoAmount(source.profitLoss),
    ]
    : [
      jitoText(source.platform),
      jitoText(source.gameType),
      jitoText(source.gameName),
      jitoAmount(source.betAmount),
    ];
  const details = [];
  if (source.gameName) details.push(['Game Name', source.gameName]);
  if (source.betLabel && source.betDetail) details.push([source.betLabel, source.betDetail]);
  if (source.betAmount != null) details.push(['Stake / Bet', jitoAmount(source.betAmount)]);
  if (source.turnover != null) details.push(['Turnover', jitoAmount(source.turnover)]);
  if (source.profitLoss != null) details.push(['Profit / Loss', jitoAmount(source.profitLoss)]);
  if (source.result) details.push(['Result', source.result]);
  if (source.status) details.push(['Status', source.status]);
  if (source.referenceId) details.push(['Bet / Reference ID', source.referenceId]);
  return { cells, details, profitLoss: source.profitLoss };
}

function recordStateFor(tab) {
  return tab === 'unsettled' ? 'Unsettled' : 'Settled';
}

export function historyRequests(applied) {
  const recordState = recordStateFor(applied && applied.tab);
  const gameType = applied && applied.gameType ? applied.gameType : 'All';
  const provider = applied && applied.provider ? applied.provider : 'All';
  return {
    sports: sportsHistoryQuery({
      gameType,
      result: 'All',
      search: '',
      from: applied && applied.from,
      to: applied && applied.to,
      recordState,
    }),
    games: gameHistoryQuery({
      page: 1,
      limit: 20,
      type: 'All',
      category: gameType,
      provider,
      result: 'All',
      status: 'All',
      search: '',
      from: applied && applied.from,
      to: applied && applied.to,
      recordState,
    }),
  };
}

function choiceList(items) {
  const seen = new Set();
  const choices = [];
  (items || []).forEach((item) => {
    const value = typeof item === 'string' ? item : item && item.value;
    const label = typeof item === 'string' ? item : (item && (item.label || item.value));
    const text = value == null ? '' : String(value).trim();
    if (!text || text === 'All' || text === '-') return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    choices.push({ value: text, label: label || text });
  });
  return choices;
}

export function gameTypeChoices(filters, sportsRecords, gameRecords) {
  const fromFilters = choiceList(filters && filters.categories);
  const fromSports = choiceList((sportsRecords || []).map((row) => row && row.gameType));
  const fromGames = choiceList((gameRecords || []).flatMap((row) => [row && row.gameType, row && row.category]));
  return choiceList(fromFilters.concat(fromSports, fromGames));
}

export function platformChoices(filters, records) {
  return choiceList(choiceList(filters && filters.providers).concat(providerChoices(records)));
}

export function presentHistoryPage(sportsRecords, gameRecords, applied) {
  const tab = applied && applied.tab === 'unsettled' ? 'unsettled' : 'settled';
  const filterState = {
    provider: applied && applied.provider ? applied.provider : 'All',
    recordState: recordStateFor(tab),
    page: 1,
  };
  const sports = filterSportsRows(sportsRecords, filterState).map((row, index) => ({
    key: `wckt:${row && row.id ? row.id : index}`,
    at: (row && (row.bettingDate || row.createdAt)) || '',
    values: sportsFieldValues(row),
  }));
  const games = filterGameRows(gameRecords, filterState).map((row, index) => ({
    key: `game:${row && (row.referenceId || row.id) ? (row.referenceId || row.id) : index}`,
    at: (row && (row.createdAt || row.bettingDate)) || '',
    values: gameFieldValues(row),
  }));
  const sorted = sports.concat(games).sort((a, b) => String(b.at).localeCompare(String(a.at)));
  const page = Math.max(1, Number(applied && applied.page) || 1);
  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1);
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  return {
    rows: sorted.slice(start, start + PAGE_SIZE).map((row) => ({
      key: row.key,
      ...arrangeBettingRow(row.values, tab),
    })),
    total,
    totalPages,
    page: safePage,
  };
}
